/**
 * Card rewards: how many points (or how much cashback) a purchase earned, from the card's
 * earn rates and the store's merchant type (MCC). Rates are yours, or suggested by the local AI.
 */
import type { CardPurchase, MoneyAccount } from "./cards.ts";
import { guessMcc, mccInfo, type MccInfo } from "../mcc.ts";
import type { GroupState, IsoDate } from "../types.ts";

export type RewardCategory = MccInfo["rewards"];

export interface CardRewards {
  unit: "points" | "cash";
  /** What a point is worth in cents (Amex MR is often valued around 2). Points only. */
  pointCents?: number;
  /** Points per dollar, or percent cashback, by merchant type. "other" is everything else. */
  rates: Partial<Record<RewardCategory, number>>;
  /**
   * Higher rates on only the first so much spending each period (BofA Customized Cash: 3% and
   * 2% on the first $2,500 a quarter combined; a first-year 6% offer is a cap with an end date).
   * The first rule that's active and covers the purchase's category applies.
   */
  caps?: CapRule[];
  source?: "you" | "ai";
}

export interface CapRule {
  label?: string;
  /** The boosted rates, by merchant type ("other" boosts everything). */
  rates: Partial<Record<RewardCategory, number>>;
  /** Spending at the boosted rates per period, shared by its categories (cents). */
  capCents: number;
  period: "month" | "quarter" | "year";
  /** Only between these dates (a first-year offer). */
  from?: IsoDate;
  until?: IsoDate;
  /** The rate past the cap (defaults to the card's "everything else" rate). */
  after?: number;
}

export const REWARD_CATEGORIES: { id: RewardCategory; label: string }[] = [
  { id: "dining", label: "Dining" },
  { id: "grocery", label: "Groceries" },
  { id: "gas", label: "Gas" },
  { id: "travel", label: "Travel and transit" },
  { id: "wholesale", label: "Costco and warehouse clubs" },
  { id: "drugstore", label: "Drugstores" },
  { id: "online", label: "Online shopping" },
  { id: "home", label: "Home improvement" },
  { id: "streaming", label: "Streaming" },
  { id: "entertainment", label: "Entertainment" },
  { id: "other", label: "Everything else" },
];

/** The merchant type of a store: its preset's code, else a guess from the name. */
export function storeCategory(store: string, states: readonly GroupState[]): RewardCategory {
  for (const s of states) {
    const code = s.stores[store]?.mcc;
    const info = mccInfo(code);
    if (info) return info.rewards;
  }
  return guessMcc(store)?.rewards ?? "other";
}

export function rateFor(rewards: CardRewards, cat: RewardCategory): number {
  return rewards.rates[cat] ?? rewards.rates.other ?? 1;
}

/** Points (whole) or cashback (cents) for one purchase. */
export function earned(rewards: CardRewards, cat: RewardCategory, totalMicro: number): number {
  const dollars = totalMicro / 1_000_000;
  const rate = rateFor(rewards, cat);
  return rewards.unit === "points" ? Math.floor(dollars * rate) : Math.round(dollars * rate); // % of dollars = cents
}

export interface RewardsSummary {
  /** Points, or cashback in cents. */
  amount: number;
  /** The value in cents (points × point value, or the cashback itself). */
  valueCents: number;
  unit: "points" | "cash";
}

function periodKey(date: IsoDate, period: CapRule["period"]): string {
  if (period === "year") return date.slice(0, 4);
  if (period === "month") return date.slice(0, 7);
  return `${date.slice(0, 4)}-Q${Math.floor((Number(date.slice(5, 7)) - 1) / 3) + 1}`;
}

const active = (c: CapRule, date: IsoDate) =>
  (!c.from || date >= c.from) && (!c.until || date <= c.until);

/**
 * Points or cashback cents for each purchase, in date order, with spending caps applied: the
 * boosted rate on the part under the period's cap, the "after" rate on the rest.
 */
export function earnedEach(
  rewards: CardRewards,
  purchases: readonly { date: IsoDate; store: string; totalMicro: number }[],
  states: readonly GroupState[],
): number[] {
  const used = new Map<string, number>();
  const order = purchases
    .map((p, i) => ({ p, i }))
    .sort((a, b) => (a.p.date < b.p.date ? -1 : a.p.date > b.p.date ? 1 : a.i - b.i));
  const out = new Array<number>(purchases.length).fill(0);
  for (const { p, i } of order) {
    const cat = storeCategory(p.store, states);
    const k = (rewards.caps ?? []).findIndex(
      (c) => active(c, p.date) && (c.rates[cat] !== undefined || c.rates.other !== undefined),
    );
    if (k < 0) {
      out[i] = earned(rewards, cat, p.totalMicro);
      continue;
    }
    const cap = rewards.caps![k]!;
    const key = `${k}:${periodKey(p.date, cap.period)}`;
    const room = Math.max(0, cap.capCents * 10_000 - (used.get(key) ?? 0));
    const boosted = Math.min(room, Math.max(0, p.totalMicro));
    used.set(key, (used.get(key) ?? 0) + boosted);
    const boostRate = cap.rates[cat] ?? cap.rates.other!;
    const afterRate = cap.after ?? rewards.rates.other ?? 1;
    const dollars = (m: number) => m / 1_000_000;
    const raw = dollars(boosted) * boostRate + dollars(p.totalMicro - boosted) * afterRate;
    out[i] = rewards.unit === "points" ? Math.floor(raw) : Math.round(raw);
  }
  return out;
}

/** How much of each cap is used in the period that holds `today` (for "$1,240 of $2,500"). */
export function capUse(
  rewards: CardRewards,
  purchases: readonly { date: IsoDate; store: string; totalMicro: number }[],
  states: readonly GroupState[],
  today: IsoDate,
): { cap: CapRule; usedCents: number }[] {
  return (rewards.caps ?? [])
    .map((cap, k) => {
      if (!active(cap, today)) return null;
      const key = periodKey(today, cap.period);
      let used = 0;
      const sorted = [...purchases].sort((a, b) => (a.date < b.date ? -1 : 1));
      for (const p of sorted) {
        if (periodKey(p.date, cap.period) !== key || !active(cap, p.date)) continue;
        const cat = storeCategory(p.store, states);
        const first = (rewards.caps ?? []).findIndex(
          (c) => active(c, p.date) && (c.rates[cat] !== undefined || c.rates.other !== undefined),
        );
        if (first === k) used += Math.max(0, p.totalMicro);
      }
      return { cap, usedCents: Math.min(cap.capCents, Math.round(used / 10_000)) };
    })
    .filter((x): x is { cap: CapRule; usedCents: number } => x !== null);
}

/**
 * What the card earned on purchases in the window (by date), with caps worked out from all of
 * the purchases you pass (so earlier spending in the quarter counts against the cap).
 */
export function rewardsFor(
  account: Pick<MoneyAccount, "id"> & { rewards?: CardRewards | null },
  purchases: readonly CardPurchase[],
  states: readonly GroupState[],
  window: { from?: IsoDate | null; to?: IsoDate | null } = {},
): RewardsSummary | null {
  const r = account.rewards;
  if (!r) return null;
  const each = earnedEach(r, purchases, states);
  let amount = 0;
  purchases.forEach((p, i) => {
    if ((window.from && p.date <= window.from) || (window.to && p.date > window.to)) return;
    amount += each[i]!;
  });
  const valueCents = r.unit === "points" ? Math.round(amount * (r.pointCents ?? 1)) : amount;
  return { amount, valueCents, unit: r.unit };
}

// ---- asking the local AI for a card's earn rates (background work)

export const REWARDS_SYSTEM = `You know US credit card reward programs. For the card named, reply with its current earn rates as JSON only:
{"unit":"points"|"cash","pointCents":number,"rates":{"dining":n,"grocery":n,"gas":n,"travel":n,"wholesale":n,"drugstore":n,"online":n,"home":n,"streaming":n,"entertainment":n,"other":n}}
Points cards: rates are points per dollar; pointCents is a typical value of one point in cents (e.g. Amex Membership Rewards about 2). Cashback cards: rates are percent back and pointCents is 1.
Warehouse clubs like Costco usually don't count as grocery. If you're unsure of the card, reply {"unknown":true}.`;

export function parseRewardsReply(text: string): CardRewards | null {
  const start = text.indexOf("{");
  const end = text.lastIndexOf("}");
  if (start < 0 || end <= start) return null;
  let raw: Record<string, unknown>;
  try {
    raw = JSON.parse(text.slice(start, end + 1)) as Record<string, unknown>;
  } catch {
    return null;
  }
  if (!raw || raw.unknown || (raw.unit !== "points" && raw.unit !== "cash")) return null;
  const rates: Partial<Record<RewardCategory, number>> = {};
  const src = (raw.rates ?? {}) as Record<string, unknown>;
  for (const { id } of REWARD_CATEGORIES) {
    const v = Number(src[id]);
    if (Number.isFinite(v) && v >= 0 && v <= 20) rates[id] = v;
  }
  if (rates.other === undefined) return null;
  const pc = Number(raw.pointCents);
  return {
    unit: raw.unit,
    ...(Number.isFinite(pc) && pc > 0 && pc <= 10 ? { pointCents: pc } : {}),
    rates,
    source: "ai",
  };
}
